import { useContext, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import ContextSelectionCard, { ContextFreshnessData, MapContext } from "./ContextSelectionCard";
import {
  GeneralServicePayload,
  DataResponse,
} from "../../hooks/useGetGeneralData";
import { useHistoricalDataContext } from "../../HistoricalDataContext";
import MultiContextHistoricalChart, {
  ContextSeriesPoint,
} from "../History/MultiContextHistoricalChart";
import { useResponsiveLayout } from "../../hooks/useResponsiveLayout";
import {
  CommunityQuestion,
  getCommunityQuestion,
  submitCommunityObservation,
} from "../../services/communityApi";
import { trackAnalyticsEvent } from "../../services/analytics";
import ParameterHelp from "../UI/ParameterHelp";
import { PARAMETER_CATALOG, type ParameterDefinition } from "../../utils/parameterCatalog";
import { getPlaceCategoryId } from "./placeCatalog";
import { defaultMobileAttributeForContext, getPlacePanelSnapTarget, PlacePanelStage } from "./mobilePlacePanel";
import { activateContextAlerts, deactivateContextAlerts } from "../../services/contextAlertsApi";
import {
  ContextThresholdRule,
  getContextThresholdRules,
  saveContextThresholdRule,
  ThresholdOperator,
  ThresholdReference,
} from "../../services/contextAlertsApi";
import { ContextHistorySummary, fetchContextHistorySummary } from "../../services/contextHistoryApi";
import { isAnonymousUser } from "../../utils/auth";
import AuthContext from "../../store/auth-context";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import {
  loadFollowedPlaceIds,
  saveFollowedPlaceIds,
  toggleFollowedPlaceId,
} from "./placeFavorites";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

type AttributeConfig = ParameterDefinition;



const CONTEXTS: MapContext[] = [
  {
    id: "sohma_la_paloma_mareografica_context",
    label: "SOHMA La Paloma",
    color: "#1E88E5", // Blue 600
    position: [-34.6649, -54.1746],
  },
  {
    id: "sohma_punta_lobos_mareografica_context",
    label: "SOHMA Punta Lobos",
    color: "#6C4AB6",
    position: [-34.865, -54.931],
  },
  {
    id: "montevideo_boya_context",
    label: "IM Boya Montevideo",
    color: "#0288D1", // Light Blue 700
    position: [-34.95788, -56.15918],
  },
  {
    id: "flowers_island_exo1_context",
    label: "Isla de Flores EXO1",
    color: "#D81B60", // Pink 600
    position: [-34.9442, -55.9322],
  },
  {
    id: "simcosta_rs4_context",
    label: "Simcosta RS-4 (Rio Grande)",
    color: "#8E24AA", // Purple 600
    position: [-32.2599361, -52.0779639],
  },
  { id: "playa_mvd_pocitos_context", label: "IM Playa Pocitos", color: "#E53935", position: [-34.9145566, -56.1477433] }, // Red 600
  { id: "playa_mvd_buceo_context", label: "IM Playa Buceo", color: "#FB8C00", position: [-34.9000656, -56.1218391] }, // Orange 600
  { id: "playa_mvd_ramirez_context", label: "IM Playa Ramírez", color: "#FDD835", position: [-34.9167086, -56.1697189] }, // Yellow 600
  { id: "playa_mvd_carrasco_context", label: "IM Playa Carrasco", color: "#7CB342", position: [-34.8879995, -56.0465748] }, // Light Green 600
  { id: "playa_mvd_malvin_context", label: "IM Playa Malvín", color: "#009688", position: [-34.8978683, -56.1009408] }, // Teal 500
  { id: "playa_mvd_arroyo_carrasco_context", label: "IM Arroyo Carrasco", color: "#546E7A", position: [-34.8521877, -56.0542171] }, // Blue Grey 600
  { id: "playa_mvd_arroyo_molino_context", label: "IM Arroyo Molino", color: "#6D4C41", position: [-34.8869938, -56.0903251] }, // Brown 600
  { id: "playa_mvd_de_los_ingleses_context", label: "IM Playa de los Ingleses", color: "#F4511E", position: [-34.8958501, -56.0849209] }, // Deep Orange 600
  { id: "playa_mvd_de_la_mulata_context", label: "IM Playa de la Mulata", color: "#C0CA33", position: [-34.8991968, -56.0679555] }, // Lime 600
  { id: "playa_mvd_verde_context", label: "IM Playa Verde", color: "#039BE5", position: [-34.898384, -56.0727453] }, // Light Blue 600
  { id: "playa_mvd_honda_context", label: "IM Playa Honda", color: "#7E57C2", position: [-34.8961014, -56.0939784] }, // Deep Purple 400
  { id: "playa_mvd_miramar_context", label: "IM Playa Miramar", color: "#FFB300", position: [-34.8658027, -56.0212387] }, // Amber 600
  { id: "playa_mvd_brava_context", label: "IM Playa Brava", color: "#F06292", position: [-34.905, -56.083] }, // Pink 300
  { id: "playa_mvd_de_los_cilindros_context", label: "IM Playa de los Cilindros", color: "#BA68C8", position: [-34.8876638, -56.3310749] }, // Purple 300
  { id: "playa_mvd_pajas_blancas_context", label: "IM Playa Pajas Blancas", color: "#4DB6AC", position: [-34.8702782, -56.3401352] }, // Teal 300
  { id: "playa_mvd_punta_yeguas_context", label: "IM Playa Punta Yeguas", color: "#9575CD", position: [-34.894595, -56.3044786] }, // Deep Purple 300
  { id: "playa_mvd_santa_catalina_context", label: "IM Playa Santa Catalina", color: "#7986CB", position: [-34.894526, -56.2909791] }, // Indigo 300
  { id: "playa_mvd_la_colorada_context", label: "IM Playa La Colorada", color: "#64B5F6", position: [-34.8584336, -56.3757289] }, // Blue 400
  { id: "playa_mvd_punta_espinillo_context", label: "IM Playa Punta Espinillo", color: "#4FC3F7", position: [-34.8267787, -56.3875821] }, // Light Blue 300
  { id: "playa_mvd_zabala_context", label: "IM Playa Zabala", color: "#4DD0E1", position: [-34.8741473, -56.3357251] }, // Cyan 300
  { id: "playa_mvd_del_cerro_context", label: "IM Playa del Cerro", color: "#81C784", position: [-34.8961939, -56.2524091] }, // Green 300
  { id: "playa_mvd_del_nacional_o_frigonal_context", label: "IM Playa del Nacional (Frigonal)", color: "#DCE775", position: [-34.7365234, -56.4530498] }, // Lime 300
  { id: "anp_muelle_fluvial_mareografo_context", label: "ANP Muelle Fluvial", color: "#00ACC1", position: [-34.9070, -56.2100] }, // Cyan 600
  { id: "anp_colonia_mareografo_context", label: "ANP Colonia", color: "#00897B", position: [-34.4720, -57.8430] }, // Teal 600
  { id: "anp_fray_bentos_mareografo_context", label: "ANP Fray Bentos", color: "#43A047", position: [-33.1160, -58.2270] }, // Green 600
  // Leve desplazamiento respecto al EXO1 para que se aprecie separado en el mapa
  { id: "anp_isla_de_flores_mareografo_context", label: "ANP Isla de Flores", color: "#3949AB", position: [-34.9442, -55.92] }, // Indigo 600
  { id: "anp_la_paloma_mareografo_context", label: "ANP La Paloma", color: "#1565C0", position: [-34.6649, -54.1646] }, // Blue 800
  { id: "anp_nueva_palmira_mareografo_context", label: "ANP Nueva Palmira", color: "#006064", position: [-33.8790, -58.4060] }, // Cyan 900
  { id: "anp_paysandu_mareografo_context", label: "ANP Paysandú", color: "#004D40", position: [-32.3130, -58.0780] }, // Teal 900
  { id: "shn_martin_garcia_mareografica_context", label: "SHN Martín García", color: "#1B5E20", position: [-34.190000, -58.256944] },
  { id: "shn_pilote_norden_mareografica_context", label: "SHN Pilote Norden", color: "#2E7D32", position: [-34.626250, -57.927417] },
  { id: "shn_oyarvide_mareografica_context", label: "SHN Oyarvide", color: "#388E3C", position: [-35.100278, -57.127500] },
  { id: "shn_buenos_aires_mareografica_context", label: "SHN Buenos Aires", color: "#43A047", position: [-34.560833, -58.398889] },
  { id: "shn_la_plata_mareografica_context", label: "SHN La Plata", color: "#4CAF50", position: [-34.833889, -57.880278] },
  { id: "shn_atalaya_mareografica_context", label: "SHN Atalaya", color: "#66BB6A", position: [-35.015278, -57.536111] },
  { id: "shn_san_fernando_mareografica_context", label: "SHN San Fernando", color: "#81C784", position: [-34.433889, -58.540278] },
  { id: "shn_san_clemente_mareografica_context", label: "SHN San Clemente del Tuyú", color: "#A5D6A7", position: [-36.354722, -56.715000] },
];


const MAX_DESKTOP_COMPARISON_ATTRIBUTES = 3;
const ATTRIBUTE_CHART_COLORS = [
  "#00796B",
  "#1565C0",
  "#EF6C00",
  "#6A1B9A",
  "#C62828",
  "#2E7D32",
];

const getAttributeChartColor = (config: AttributeConfig) => {
  const configIndex = Math.max(0, PARAMETER_CATALOG.indexOf(config));
  return ATTRIBUTE_CHART_COLORS[configIndex % ATTRIBUTE_CHART_COLORS.length];
};

const formatDateTime = (date: Date) =>
  new Intl.DateTimeFormat("es-UY", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);

async function fetchGeneralData(
  contextId: string,
  fechaDesde: string,
  fechaHasta: string
): Promise<GeneralServicePayload> {
  const baseUrl = process.env.REACT_APP_API_HOST ?? "";
  const timeoutMs = parseInt(process.env.REACT_APP_OBS_TIMEOUT_MS || "15000", 10);
  const mapResponse = (responseData: any[] = []) =>
    responseData.map((dataBack: any) => ({
      temperatura: dataBack.temperatura,
      temperatura_nasa: dataBack.temperatura_nasa,
      conductividad: dataBack.conductividad,
      turbidez: dataBack.turbidez,
      salinidad: dataBack.salinidad,
      profundidad: dataBack.profundidad,
      orp: dataBack.orp,
      ph: dataBack.ph,
      clorofila: dataBack.clorofila,
      oxigeno_disuelto: dataBack.oxigeno_disuelto,
      oxigeno_saturacion: dataBack.oxigeno_saturacion,
      nitrato: dataBack.nitrato,
      amonio: dataBack.amonio,
      cianobacterias: dataBack.cianobacterias,
      coliformes_termotolerantes: dataBack.coliformes_termotolerantes,
      enterococos_ecocos: dataBack.enterococos_ecocos,
      enterococos_mg5: dataBack.enterococos_mg5,
      temperatura_agua: dataBack.temperatura_agua,
      temperatura_ambiente: dataBack.temperatura_ambiente,
      presion_atmosferica: dataBack.presion_atmosferica,
      precipitacion: dataBack.precipitacion,
      velocidad_viento: dataBack.velocidad_viento,
      direccion_viento: dataBack.direccion_viento,
      velocidad_corriente: dataBack.velocidad_corriente,
      direccion_corriente: dataBack.direccion_corriente,
      ola_altura_significativa: dataBack.ola_altura_significativa,
      ola_altura_maxima: dataBack.ola_altura_maxima,
      ola_direccion_media: dataBack.ola_direccion_media,
      ola_periodo_pico: dataBack.ola_periodo_pico,
      bateria_porcentaje: dataBack.bateria_porcentaje,
      bateria_wh: dataBack.bateria_wh,
      nivel_mar: dataBack.nivel_mar,
      voltaje_device: dataBack.voltaje_device ?? dataBack.voltajeDevice,
      temperatura_device: dataBack.temperatura_device ?? dataBack.temperaturaDevice,
      latitud: dataBack.latitud,
      longitud: dataBack.longitud,
      recvTime: dataBack.recvTime,
      timestamp: dataBack.timestamp,
    }));

  const mainResp = await axios.get(`${baseUrl}/api/v1/contexts/${contextId}`, {
    headers: { "Access-Control-Allow-Origin": "*" },
    params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta },
    timeout: timeoutMs,
  });

  const mainData = mapResponse(mainResp.data.data || []);
  if (mainData.length > 0) {
    return { data: mainData, reduced: Boolean(mainResp.data.reduced) };
  }

  try {
    const lastResp = await axios.get(`${baseUrl}/api/v1/contexts/${contextId}/last`, {
      headers: { "Access-Control-Allow-Origin": "*" },
      timeout: timeoutMs,
    });
    const lastData = mapResponse(lastResp.data.data || []);
    return { data: lastData, reduced: Boolean(lastResp.data.reduced) };
  } catch (err) {
    console.error("Error fetching last measurement", err);
    return { data: mainData, reduced: Boolean(mainResp.data.reduced) };
  }
}

interface MobilePlaceAttribute {
  key: keyof DataResponse;
  config: AttributeConfig;
  points: ContextSeriesPoint[];
}

type FollowToggleResult = {
  followed: boolean;
  persisted: boolean;
};

interface MobilePlacePanelProps {
  context: MapContext;
  payload: GeneralServicePayload | null;
  loading: boolean;
  historySummary: ContextHistorySummary | null;
  historyLoading: boolean;
  historyDays: number;
  selectedAttributeKeys: string[];
  onAttributeSelectionChange: (keys: string[]) => void;
  isDesktop: boolean;
  selectedRangeHours: number;
  onRangeChange: (hours: number) => void;
  onClose: () => void;
  onNavigate: (direction: -1 | 1) => void;
  anonymousUser: boolean;
  userEmail: string;
  token?: string | null;
  isFollowed: boolean;
  onFollowPlace: (
    contextId: string,
    attributes: string[],
  ) => Promise<FollowToggleResult>;
  onUnfollowPlace: (contextId: string) => Promise<FollowToggleResult>;
  placeIndex: number;
  placeCount: number;
}

const formatOptionalDate = (date: Date | undefined) => {
  if (!date || Number.isNaN(date.getTime())) {
    return "No disponible";
  }
  return formatDateTime(date);
};
const communityErrorMessage = (caught: unknown, fallback: string) => {
  if (!axios.isAxiosError(caught)) return fallback;
  if (caught.response?.status === 404) {
    return "El circuito de aportes todavía no está habilitado en este servidor.";
  }
  const data: unknown = caught.response?.data;
  if (data && typeof data === "object" && "detail" in data && typeof data.detail === "string") {
    return data.detail;
  }
  return fallback;
};
const getHistoricalReferenceValue = (
  kind: ThresholdReference["kind"],
  summary?: ContextHistorySummary["attributes"][string],
): number | undefined => {
  if (!summary) return undefined;
  if (kind === "min") return summary.min.value;
  if (kind === "max") return summary.max.value;
  if (kind === "p05") return summary.percentiles.p05;
  if (kind === "p95") return summary.percentiles.p95;
  return undefined;
};


const MobilePlacePanel = ({
  context,
  payload,
  loading,
  historySummary,
  historyLoading,
  historyDays,
  selectedAttributeKeys,
  onAttributeSelectionChange,
  isDesktop,
  selectedRangeHours,
  onRangeChange,
  onClose,
  onNavigate,
  isFollowed,
  anonymousUser,
  userEmail,
  token,
  onFollowPlace,
  onUnfollowPlace,
  placeIndex,
  placeCount,
}: MobilePlacePanelProps) => {
  const navigate = useNavigate();
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [contributeOpen, setContributeOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [followStatus, setFollowStatus] = useState<"idle" | "saving">("idle");
  const [followError, setFollowError] = useState<string | null>(null);
  const [followMessage, setFollowMessage] = useState<string | null>(null);
  const [attributePickerAnchorEl, setAttributePickerAnchorEl] = useState<HTMLElement | null>(null);
  const [thresholdOpen, setThresholdOpen] = useState(false);
  const [thresholdAttributeKey, setThresholdAttributeKey] = useState<string | null>(null);
  const [thresholdOperator, setThresholdOperator] = useState<ThresholdOperator>("gte");
  const [thresholdValue, setThresholdValue] = useState("");
  const [thresholdValueEdited, setThresholdValueEdited] = useState(false);
  const [thresholdReferenceKind, setThresholdReferenceKind] = useState<ThresholdReference["kind"]>("max");
  const [thresholdNotifyOnRecovery, setThresholdNotifyOnRecovery] = useState(true);
  const [thresholdRules, setThresholdRules] = useState<ContextThresholdRule[]>([]);
  const [thresholdStatus, setThresholdStatus] = useState<"idle" | "loading" | "saving" | "success" | "error">("idle");
  const [thresholdError, setThresholdError] = useState<string | null>(null);
  const [thresholdMessage, setThresholdMessage] = useState<string | null>(null);
  useEffect(() => {
    setFollowMessage(null);
    setFollowError(null);
  }, [context.id]);
  const [contributionQuestion, setContributionQuestion] = useState<CommunityQuestion | null>(null);
  const [contributionAnswer, setContributionAnswer] = useState("");
  const [contributionStatus, setContributionStatus] = useState<
    "idle" | "loading" | "ready" | "submitting" | "success" | "error"
  >("idle");
  const [contributionError, setContributionError] = useState<string | null>(null);
  const swipeOffsetRef = useRef(0);
  const swipeRef = useRef<{
    startX: number;
    startY: number;
    axis: "pending" | "horizontal" | "vertical";
  } | null>(null);
  const canNavigatePrevious = placeIndex > 0;
  const canNavigateNext = placeIndex < placeCount - 1;
  const loadContributionQuestion = async () => {
    setContributionStatus("loading");
    setContributionError(null);
    try {
      const selection = await getCommunityQuestion(context.id);
      setContributionQuestion(selection.question);
      setContributionAnswer("");
      setContributionStatus("ready");
    } catch (caught) {
      const detail = communityErrorMessage(caught, "No se pudo cargar una pregunta para esta estación.");
      setContributionError(detail);
      setContributionStatus("error");
    }
  };

  const openContribution = () => {
    trackAnalyticsEvent("community_report_started", { report_type: "place_observation" });
    setContributeOpen(true);
    setContributionQuestion(null);
    setContributionAnswer("");
    setContributionError(null);
    setContributionStatus("idle");
  };

  const closeContribution = () => {
    if (contributionStatus === "submitting") return;
    setContributeOpen(false);
  };

  const submitContribution = async () => {
    if (!contributionQuestion || !contributionAnswer) return;
    setContributionStatus("submitting");
    setContributionError(null);
    try {
      await submitCommunityObservation({
        context_id: context.id,
        question_id: contributionQuestion.id,
        answer: contributionAnswer,
        observed_at: new Date().toISOString(),
        place_confirmed: true,
        location_accessed: false,
        idempotency_key: crypto.randomUUID(),
      });
      trackAnalyticsEvent("community_report_submitted", {
        report_type: "place_observation",
        success: true,
      });
      setContributionStatus("success");
    } catch (caught) {
      trackAnalyticsEvent("community_report_submitted", {
        report_type: "place_observation",
        success: false,
      });
      const detail = communityErrorMessage(caught, "No se pudo enviar la observación. El borrador no se publicó.");
      setContributionError(detail);
      setContributionStatus("error");
    }
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("button, a, input, canvas, [role='button']")) {
      return;
    }
    swipeOffsetRef.current = 0;
    setSwipeOffset(0);
    swipeRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      axis: "pending",
    };
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const swipe = swipeRef.current;
    if (!swipe) return;

    const deltaX = event.clientX - swipe.startX;
    const deltaY = event.clientY - swipe.startY;
    if (swipe.axis === "pending") {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 8) return;
      swipe.axis = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
      if (swipe.axis === "horizontal") {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    }
    if (swipe.axis !== "horizontal") return;

    event.preventDefault();
    const direction = deltaX < 0 ? 1 : -1;
    const canMove = direction === -1 ? canNavigatePrevious : canNavigateNext;
    const nextOffset = canMove ? deltaX : deltaX * 0.24;
    swipeOffsetRef.current = nextOffset;
    setSwipeOffset(nextOffset);
  };
  const handlePointerEnd = () => {
    const swipe = swipeRef.current;
    swipeRef.current = null;
    const offset = swipeOffsetRef.current;
    swipeOffsetRef.current = 0;
    if (!swipe || swipe.axis !== "horizontal") {
      setSwipeOffset(0);
      return;
    }

    const direction = offset < 0 ? 1 : -1;
    const canMove = direction === -1 ? canNavigatePrevious : canNavigateNext;
    const shouldNavigate = Math.abs(offset) >= 72 && canMove;
    setSwipeOffset(0);
    if (shouldNavigate) {
      onNavigate(direction);
    }
  };


  const availableAttributes = useMemo<MobilePlaceAttribute[]>(() => {
    if (!payload) {
      return [];
    }

    return PARAMETER_CATALOG.flatMap((config) => {
      const key = config.keys.find((candidate) =>
        payload.data.some((row) => typeof row[candidate] === "number")
      );
      if (!key) {
        return [];
      }

      const points = payload.data
        .filter((row) => typeof row[key] === "number")
        .map((row) => ({
          value: row[key] as number,
          recvTime: new Date(row.recvTime),
          timestamp: row.timestamp ? new Date(row.timestamp) : undefined,
        }))
        .sort((a, b) => {
          const aDate = a.timestamp || a.recvTime;
          const bDate = b.timestamp || b.recvTime;
          return aDate.getTime() - bDate.getTime();
        });

      return points.length > 0 ? [{ key, config, points }] : [];
    });
  }, [payload]);

  const selectedAttributes = useMemo(
    () => availableAttributes.filter((attribute) =>
      selectedAttributeKeys.some((selectedKey) =>
        String(attribute.key) === selectedKey
        || attribute.config.keys.some((candidate) => String(candidate) === selectedKey)
      )
    ),
    [availableAttributes, selectedAttributeKeys],
  );
  const selectedAttribute = selectedAttributes[0] || availableAttributes[0];
  const chartAttributes = isDesktop
    ? selectedAttributes.length > 0
      ? selectedAttributes
      : selectedAttribute
        ? [selectedAttribute]
        : []
    : selectedAttribute
      ? [selectedAttribute]
      : [];
  const selectedAttributeKeysForToggle = chartAttributes.map((attribute) => String(attribute.key));
  const handleAttributeToggle = (key: string) => {
    if (!isDesktop) {
      onAttributeSelectionChange([key]);
      return;
    }

    if (selectedAttributeKeysForToggle.includes(key)) {
      if (selectedAttributeKeysForToggle.length > 1) {
        onAttributeSelectionChange(selectedAttributeKeysForToggle.filter((selectedKey) => selectedKey !== key));
      }
      return;
    }

    if (selectedAttributeKeysForToggle.length < MAX_DESKTOP_COMPARISON_ATTRIBUTES) {
      onAttributeSelectionChange([...selectedAttributeKeysForToggle, key]);
    }
  };
  const latestPoint = selectedAttribute?.points[selectedAttribute.points.length - 1];
  const latestSourceRow = useMemo(
    () => payload?.data
      .slice()
      .sort((a, b) => {
        const aTime = Date.parse(a.timestamp || a.recvTime.toISOString());
        const bTime = Date.parse(b.timestamp || b.recvTime.toISOString());
        return aTime - bTime;
      })
      .at(-1),
    [payload],
  );
  const latestSourceStatus = latestSourceRow?.source_status;
  const historyForAttribute = (attribute: MobilePlaceAttribute | undefined) => {
    if (!attribute || !historySummary) return undefined;
    return attribute.config.keys
      .map((key) => historySummary.attributes[String(key)])
      .find(Boolean);
  };
  const thresholdAttribute = availableAttributes.find(
    (attribute) => String(attribute.key) === thresholdAttributeKey,
  ) || selectedAttribute;
  const thresholdHistory = historyForAttribute(thresholdAttribute);
  const activeThresholdRule = thresholdRules.find(
    (rule) => rule.enabled && rule.ctxAttribute === String(thresholdAttribute?.key),
  );
  const formatThresholdValue = (value: number | undefined) =>
    typeof value === "number"
      ? value.toLocaleString("es-UY", { maximumFractionDigits: 2 })
      : "No disponible";
  const formatHistoricalDate = (measuredAt: string | null) => {
    if (!measuredAt) return "fecha no disponible";
    const date = new Date(measuredAt);
    return Number.isNaN(date.getTime()) ? "fecha no disponible" : formatDateTime(date);
  };
  const formatHistoricalReference = (
    reference: ContextHistorySummary["attributes"][string]["min"] | undefined,
    unit = thresholdAttribute?.config.unit || "",
  ) => {
    if (!reference) return "No disponible";
    const repetitions = reference.occurrenceCount > 1
      ? ` · ${reference.occurrenceCount} veces`
      : "";
    return `${formatThresholdValue(reference.value)} ${unit} — ${formatHistoricalDate(reference.measuredAt)}${repetitions}`;
  };
  const chooseThresholdReference = (kind: ThresholdReference["kind"]) => {
    setThresholdReferenceKind(kind);
    setThresholdValueEdited(true);
    const value = getHistoricalReferenceValue(kind, thresholdHistory);
    if (value !== undefined) setThresholdValue(String(value));
  };
  const openThresholdConfiguration = (attribute = selectedAttribute) => {
    if (anonymousUser) {
      setAccountDialogOpen(true);
      return;
    }
    setThresholdAttributeKey(attribute ? String(attribute.key) : null);
    setThresholdValueEdited(false);
    setThresholdStatus("loading");
    setThresholdError(null);
    setThresholdMessage(null);
    setThresholdOpen(true);
    void getContextThresholdRules({
      contextId: context.id,
      user: userEmail,
      token,
    }).then((rules) => {
      setThresholdRules(rules);
      const existing = rules.find(
        (rule) => rule.enabled && rule.ctxAttribute === String(attribute?.key),
      );
      const operator = existing?.operator || "gte";
      const referenceKind = existing?.reference?.kind || "max";
      const history = historyForAttribute(attribute);
      setThresholdOperator(operator);
      setThresholdNotifyOnRecovery(existing?.notifyOnRecovery ?? true);
      setThresholdReferenceKind(referenceKind);
      setThresholdValueEdited(Boolean(existing));
      setThresholdValue(String(
        existing?.threshold
        ?? getHistoricalReferenceValue(referenceKind, history)
        ?? "",
      ));
      setThresholdStatus("idle");
    }).catch((caught) => {
      setThresholdStatus("error");
      setThresholdError(caught instanceof Error ? caught.message : "No se pudieron cargar las alertas.");
    });
  };
  const saveThreshold = async () => {
    if (!thresholdAttribute || !userEmail) return;
    const value = Number(thresholdValue);
    if (!Number.isFinite(value)) {
      setThresholdStatus("error");
      setThresholdError("Ingresá un valor de umbral válido.");
      return;
    }
    const historicalReference = thresholdHistory
      && (thresholdReferenceKind === "min" || thresholdReferenceKind === "max")
      ? thresholdHistory[thresholdReferenceKind]
      : undefined;
    const reference: ThresholdReference = {
      kind: thresholdReferenceKind,
      windowDays: historyDays,
      calculatedAt: new Date().toISOString(),
      value: historicalReference?.value ?? value,
      measuredAt: historicalReference?.measuredAt,
      occurrenceCount: historicalReference?.occurrenceCount,
    };
    setThresholdStatus("saving");
    setThresholdError(null);
    try {
      const saved = await saveContextThresholdRule({
        contextId: context.id,
        user: userEmail,
        attribute: String(thresholdAttribute.key),
        threshold: value,
        operator: thresholdOperator,
        notifyOnRecovery: thresholdNotifyOnRecovery,
        reference,
        token,
      });
      setThresholdRules((previous) => [
        ...previous.filter((rule) => rule.ruleTagName !== saved.ruleTagName),
        saved,
      ]);
      setThresholdStatus("success");
      setThresholdMessage(`Alerta guardada: ${thresholdAttribute.config.label} ${thresholdOperator === "gte" ? "≥" : "≤"} ${formatThresholdValue(value)} ${thresholdAttribute.config.unit || ""}`);
    } catch (caught) {
      setThresholdStatus("error");
      setThresholdError(caught instanceof Error ? caught.message : "No se pudo guardar la alerta.");
    }
  };
  useEffect(() => {
    if (!thresholdOpen || thresholdStatus !== "idle" || thresholdValueEdited || !thresholdHistory) return;
    const defaultKind: "min" | "max" = "max";
    const reference = thresholdHistory[defaultKind];
    setThresholdReferenceKind(defaultKind);
    setThresholdValue(String(reference.value));
  }, [thresholdHistory, thresholdOpen, thresholdOperator, thresholdStatus, thresholdValueEdited]);
  const activateFollow = async () => {
    if (availableAttributes.length === 0) {
      setFollowError("Esta estación no tiene atributos medidos para activar alertas.");
      return;
    }

    setFollowStatus("saving");
    setFollowError(null);
    try {
      const result = await onFollowPlace(
        context.id,
        availableAttributes.map(({ key }) => String(key)),
      );
      setFollowStatus("idle");
      setFollowMessage(
        `Estación seguida. Se activaron Staleness, Freshness y ${availableAttributes.length} atributo(s).${
          result.persisted
            ? ""
            : " No se pudo guardar el seguimiento en este dispositivo; queda activo durante esta sesión."
        }`,
      );
    } catch (caught) {
      setFollowStatus("idle");
      setFollowError(caught instanceof Error ? caught.message : "No se pudieron activar las alertas.");
    }
  };
  const periodOptions = [
    { label: "7 D", value: 24 * 7 },
    { label: "1 M", value: 24 * 30 },
    { label: "3 M", value: 24 * 30 * 3 },
    { label: "6 M", value: 24 * 30 * 6 },
    { label: "9 M", value: 24 * 30 * 9 },
    { label: "1 A", value: 24 * 365 },
    { label: "18 M", value: 24 * 30 * 18 },
    { label: "2 A", value: 24 * 365 * 2 },
  ];

  return (
    <Stack
      spacing={2}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      sx={{
        width: "100%",
        pb: 1,
        transform: `translateX(${swipeOffset}px)`,
        transition: swipeOffset === 0 ? "transform 180ms ease-out" : "none",
        touchAction: "pan-y",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          aria-label="Cerrar estación"
          onClick={onClose}
          sx={{ minWidth: 44, minHeight: 44, flexShrink: 0 }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 1.08,
              overflowWrap: "anywhere",
            }}
          >
            {context.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Estación de monitoreo · {placeIndex + 1} de {placeCount}
          </Typography>
        </Box>
        <IconButton
          aria-label="Estación anterior"
          onClick={() => onNavigate(-1)}
          disabled={!canNavigatePrevious}
          size="small"
        >
          <ChevronLeftRoundedIcon />
        </IconButton>
        <IconButton
          aria-label="Estación siguiente"
          onClick={() => onNavigate(1)}
          disabled={!canNavigateNext}
          size="small"
        >
          <ChevronRightRoundedIcon />
        </IconButton>
      </Stack>
      {loading && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          role="status"
          aria-live="polite"
          sx={{ px: 1, minHeight: 40 }}
        >
          <CircularProgress
            size={24}
            thickness={5}
            aria-label="Cargando datos"
            sx={{
              color: (theme) => theme.palette.mode === "dark" ? "#a6e8d2" : "#166951",
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Cargando datos…
          </Typography>
        </Stack>
      )}
      <Stack
        spacing={0.75}
        sx={{
          p: 1.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: (theme) =>
            theme.palette.mode === "dark" ? "#2b5b68" : "#cfe4dc",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(7, 29, 38, 0.42)"
              : "rgba(237, 245, 243, 0.86)",
        }}
      >
        <Button
          variant={isFollowed ? "contained" : "outlined"}
          startIcon={followStatus === "saving" ? <CircularProgress size={16} color="inherit" /> : isFollowed ? <BookmarkRoundedIcon /> : <BookmarkBorderRoundedIcon />}
          onClick={() => {
            if (isFollowed) {
              setFollowStatus("saving");
              void onUnfollowPlace(context.id)
                .then((result) => {
                  setFollowMessage(
                    result.persisted
                      ? "Estación quitada de tu lista de estaciones seguidas."
                      : "Estación quitada de esta sesión.",
                  );
                })
                .catch((caught) => {
                  setFollowError(caught instanceof Error ? caught.message : "No se pudo quitar el seguimiento.");
                })
                .finally(() => setFollowStatus("idle"));
              return;
            }
            if (anonymousUser) {
              setAccountDialogOpen(true);
              return;
            }
            void activateFollow();
          }}
          disabled={loading || followStatus === "saving"}
          sx={{
            minHeight: 46,
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 3,
            color: (theme) =>
              isFollowed
                ? theme.palette.mode === "dark" ? "#071d26" : "#ffffff"
                : theme.palette.mode === "dark" ? "#a6e8d2" : "#166951",
            borderColor: (theme) =>
              theme.palette.mode === "dark" ? "#6e9a9b" : "#7eb7ac",
            backgroundColor: (theme) =>
              isFollowed && theme.palette.mode === "dark" ? "#a6e8d2" : undefined,
            "&:hover": {
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "#a6e8d2" : "#166951",
              backgroundColor: (theme) =>
                isFollowed
                  ? theme.palette.mode === "dark" ? "#c1f5e5" : "#0f573f"
                  : theme.palette.mode === "dark" ? "rgba(166, 232, 210, 0.12)" : "rgba(22, 105, 81, 0.08)",
            },
          }}
        >
          {followStatus === "saving"
            ? isFollowed ? "Desactivando alertas…" : "Activando alertas…"
            : isFollowed ? "Siguiendo estación" : "Seguir estación"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Seguir estación guarda este punto en tu lista y activa alertas de Staleness y Freshness,
          además de mediana±ε, rate of change, valor congelado y nuevas observaciones comunitarias.
        </Typography>
        {followMessage && (
          <Typography
            role="status"
            variant="caption"
            sx={{
              color: (theme) =>
                theme.palette.mode === "dark" ? "#a6e8d2" : "#166951",
            }}
          >
            {followMessage}
          </Typography>
        )}
        {followError && <Alert severity="error">{followError}</Alert>}
      </Stack>
      <Dialog
        open={accountDialogOpen}
        onClose={() => setAccountDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="follow-account-dialog-title"
      >
        <DialogTitle id="follow-account-dialog-title">
          Configuración de alertas
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            El acceso anónimo es temporal y no permite seguir estaciones ni guardar una configuración de alertas personal.
          </Alert>
          <Typography variant="body2">
            Para seguir esta estación o configurar alertas personalizadas, ingresá o registrá una cuenta.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)}>Cerrar</Button>
          <Button
            onClick={() => {
              setAccountDialogOpen(false);
              navigate("/");
            }}
          >
            Ingresar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setAccountDialogOpen(false);
              navigate("/register");
            }}
          >
            Registrarme
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={thresholdOpen}
        onClose={() => {
          if (thresholdStatus !== "saving") setThresholdOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="threshold-dialog-title"
      >
        <DialogTitle id="threshold-dialog-title">
          Alerta por {thresholdAttribute?.config.label || "parámetro"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {historyLoading && (
              <Typography variant="body2" color="text.secondary">
                Cargando referencias de los últimos {historyDays} días…
              </Typography>
            )}
            {thresholdAttribute && (
              <>
                <Typography variant="subtitle2">
                  {thresholdAttribute.config.label} · Últimos {historyDays} días
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Último valor: {formatThresholdValue(latestPoint?.value)} {thresholdAttribute.config.unit}
                </Typography>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    {thresholdOperator === "gte" ? "Avisarme cuando supere" : "Avisarme cuando baje de"}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={thresholdOperator}
                    onChange={(event) => {
                      const nextOperator = event.target.value as ThresholdOperator;
                      setThresholdOperator(nextOperator);
                      if (!thresholdValueEdited) {
                        const nextKind: "min" | "max" = "max";
                        const nextReference = thresholdHistory?.[nextKind];
                        setThresholdReferenceKind(nextKind);
                        if (nextReference) setThresholdValue(String(nextReference.value));
                      }
                    }}
                  >
                    <FormControlLabel value="gte" control={<Radio />} label="Por encima" />
                    <FormControlLabel value="lte" control={<Radio />} label="Por debajo" />
                  </RadioGroup>
                </FormControl>
                <TextField
                  label="Valor umbral"
                  type="number"
                  value={thresholdValue}
                  onChange={(event) => {
                    setThresholdValueEdited(true);
                    setThresholdReferenceKind("custom");
                    setThresholdValue(event.target.value);
                  }}
                  inputProps={{ step: "any" }}
                  fullWidth
                  helperText={`Unidad: ${thresholdAttribute.config.unit || "sin unidad"}`}
                />
                {thresholdHistory && (
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      {thresholdAttribute.config.label} · Últimos {historyDays} días · {thresholdHistory.sampleCount} muestras
                    </Typography>
                    <Typography variant="body2">
                      Mínimo: {formatHistoricalReference(thresholdHistory.min)}
                    </Typography>
                    <Typography variant="body2">
                      Máximo: {formatHistoricalReference(thresholdHistory.max)}
                    </Typography>
                    <Typography variant="body2">
                      Mediana: {formatThresholdValue(thresholdHistory.percentiles.p50)} {thresholdAttribute.config.unit}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{
                        "& .MuiButton-outlined": {
                          color: "secondary.main",
                          borderColor: "secondary.main",
                          "&:hover": {
                            borderColor: "secondary.light",
                            backgroundColor: "action.hover",
                          },
                        },
                        "& .MuiButton-contained": {
                          backgroundColor: "secondary.main",
                          color: "secondary.contrastText",
                          "&:hover": { backgroundColor: "secondary.light" },
                        },
                      }}
                    >
                      {thresholdOperator === "gte" ? (
                        <>
                          <Button size="small" variant={thresholdReferenceKind === "p95" ? "contained" : "outlined"} onClick={() => chooseThresholdReference("p95")}>
                            P95 · {formatThresholdValue(thresholdHistory.percentiles.p95)}
                          </Button>
                          <Button size="small" variant={thresholdReferenceKind === "max" ? "contained" : "outlined"} onClick={() => chooseThresholdReference("max")}>
                            Máximo · {formatThresholdValue(thresholdHistory.max.value)}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="small" variant={thresholdReferenceKind === "p05" ? "contained" : "outlined"} onClick={() => chooseThresholdReference("p05")}>
                            P05 · {formatThresholdValue(thresholdHistory.percentiles.p05)}
                          </Button>
                          <Button size="small" variant={thresholdReferenceKind === "min" ? "contained" : "outlined"} onClick={() => chooseThresholdReference("min")}>
                            Mínimo · {formatThresholdValue(thresholdHistory.min.value)}
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={thresholdNotifyOnRecovery}
                      onChange={(event) => setThresholdNotifyOnRecovery(event.target.checked)}
                    />
                  }
                  label="Avisarme cuando vuelva al rango"
                />
                {activeThresholdRule && (
                  <Typography variant="caption" color="text.secondary">
                    Alerta actual: {activeThresholdRule.operator === "gte" ? "≥" : "≤"} {formatThresholdValue(activeThresholdRule.threshold)} {thresholdAttribute.config.unit}
                  </Typography>
                )}
                <Alert severity="info">
                  La referencia histórica sugiere un valor; la alerta guardará ese umbral como valor fijo.
                </Alert>
              </>
            )}
            {thresholdStatus === "error" && <Alert severity="error">{thresholdError}</Alert>}
            {thresholdStatus === "success" && <Alert severity="success">{thresholdMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThresholdOpen(false)} disabled={thresholdStatus === "saving"}>Cerrar</Button>
          <Button
            variant="contained"
            onClick={() => void saveThreshold()}
            disabled={!thresholdAttribute || historyLoading || thresholdStatus === "loading" || thresholdStatus === "saving"}
          >
            {thresholdStatus === "saving" ? "Guardando…" : "Guardar alerta"}
          </Button>
        </DialogActions>
      </Dialog>
      <Stack
        spacing={1}
        sx={{
          p: 1.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: (theme) =>
            theme.palette.mode === "dark" ? "#2b5b68" : "#cfe4dc",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(7, 29, 38, 0.42)"
              : "rgba(237, 245, 243, 0.86)",
        }}
      >
        <Button
          variant="contained"
          onClick={openContribution}
          sx={{
            minHeight: 48,
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 3,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#a6e8d2" : "#166951",
            color: (theme) =>
              theme.palette.mode === "dark" ? "#071d26" : "#ffffff",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#c1f5e5" : "#0f573f",
              boxShadow: "none",
            },
          }}
        >
          Estoy acá · aportar
        </Button>
        <Typography variant="caption" color="text.secondary">
          Observación ciudadana, separada de las mediciones instrumentales.
        </Typography>
      </Stack>

      <Dialog
        open={contributeOpen}
        onClose={closeContribution}
        fullWidth
        maxWidth="xs"
        aria-labelledby="contribute-dialog-title"
      >
        <DialogTitle id="contribute-dialog-title">
          Aportar en {context.label}
        </DialogTitle>
        <DialogContent dividers>
          {contributionStatus === "idle" && (
            <Stack spacing={1.5}>
              <Typography variant="body1">
                Confirmá que estás en esta estación ahora. Tu respuesta se publica
                en el circuito de ingesta de esta estación, separada de las mediciones instrumentales.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No compartimos tu ubicación precisa: solamente la estación que confirmes y la hora de tu observación.
              </Typography>
            </Stack>
          )}
          {contributionStatus === "loading" && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={22} />
              <Typography>Cargando una pregunta breve…</Typography>
            </Stack>
          )}
          {(contributionStatus === "ready" || contributionStatus === "submitting") && contributionQuestion && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Estación confirmada: {context.label}
              </Typography>
              <FormControl>
                <FormLabel id="community-question-label">{contributionQuestion.prompt}</FormLabel>
                <RadioGroup
                  aria-labelledby="community-question-label"
                  value={contributionAnswer}
                  onChange={(event) => setContributionAnswer(event.target.value)}
                >
                  {contributionQuestion.options.map((option) => (
                    <FormControlLabel
                      key={option.id}
                      value={option.id}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                {contributionQuestion.guidance}
              </Typography>
            </Stack>
          )}
          {contributionStatus === "success" && (
            <Stack spacing={1}>
              <Typography variant="h6">Gracias, sumaste una observación de esta estación.</Typography>
              <Typography variant="body2" color="text.secondary">
                La observación quedó publicada en el circuito de ingesta de la estación.
              </Typography>
            </Stack>
          )}
          {contributionStatus === "error" && (
            <Alert severity="error">{contributionError || "No se pudo completar el aporte."}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          {contributionStatus === "idle" && (
            <>
              <Button onClick={closeContribution}>Ahora no</Button>
              <Button variant="contained" onClick={() => void loadContributionQuestion()} autoFocus>
                Sí, estoy acá
              </Button>
            </>
          )}
          {contributionStatus === "loading" && <Button disabled>Preparando…</Button>}
          {(contributionStatus === "ready" || contributionStatus === "submitting") && (
            <>
              <Button onClick={closeContribution} disabled={contributionStatus === "submitting"}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={() => void submitContribution()}
                disabled={!contributionAnswer || contributionStatus === "submitting"}
              >
                {contributionStatus === "submitting" ? "Enviando…" : "Enviar observación"}
              </Button>
            </>
          )}
          {contributionStatus === "error" && (
            <>
              <Button onClick={closeContribution}>Cerrar</Button>
              <Button variant="contained" onClick={() => void loadContributionQuestion()}>
                Reintentar
              </Button>
            </>
          )}
          {contributionStatus === "success" && (
            <>
              <Button onClick={closeContribution}>Ver la estación</Button>
              <Button variant="contained" onClick={openContribution}>Aportar otra</Button>
            </>
          )}
        </DialogActions>
      </Dialog>


      {!loading && availableAttributes.length === 0 && (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No hay mediciones disponibles para esta estación en el período consultado.
          </Typography>
        </Box>
      )}

      {availableAttributes.length > 0 && (
        <>
          <Box>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                mb: 0.75,
                fontWeight: 800,
                letterSpacing: "0.12em",
                color: "text.secondary",
              }}
            >
              {isDesktop ? "Parámetros del gráfico" : "Parámetros disponibles"}
            </Typography>
            {isDesktop ? (
              <>
                <Stack spacing={1}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    flexWrap="wrap"
                  >
                    {chartAttributes.map(({ key, config }) => (
                      <Chip
                        key={String(key)}
                        label={`${config.label}${config.unit ? ` · ${config.unit}` : ""}`}
                        onDelete={() => handleAttributeToggle(String(key))}
                        sx={{ maxWidth: "100%" }}
                      />
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddRoundedIcon />}
                      onClick={(event) => setAttributePickerAnchorEl(event.currentTarget)}
                      disabled={chartAttributes.length >= MAX_DESKTOP_COMPARISON_ATTRIBUTES}
                      sx={{ alignSelf: { xs: "flex-start", sm: "center" }, textTransform: "none", borderRadius: 2 }}
                    >
                      Agregar parámetro
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Hasta {MAX_DESKTOP_COMPARISON_ATTRIBUTES} parámetros. Las unidades se muestran en ejes separados.
                  </Typography>
                </Stack>
                <Popover
                  open={Boolean(attributePickerAnchorEl)}
                  anchorEl={attributePickerAnchorEl}
                  onClose={() => setAttributePickerAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <Box sx={{ p: 1.5, width: { xs: 280, sm: 360 }, maxWidth: "calc(100vw - 32px)" }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Comparar parámetros
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Seleccioná hasta {MAX_DESKTOP_COMPARISON_ATTRIBUTES} parámetros para compartir el mismo período.
                    </Typography>
                    <Stack>
                      {availableAttributes.map(({ key, config }) => {
                        const checked = selectedAttributeKeysForToggle.includes(String(key));
                        return (
                          <FormControlLabel
                            key={String(key)}
                            control={(
                              <Checkbox
                                checked={checked}
                                disabled={!checked && chartAttributes.length >= MAX_DESKTOP_COMPARISON_ATTRIBUTES}
                                onChange={() => handleAttributeToggle(String(key))}
                              />
                            )}
                            label={(
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="body2">
                                  {config.label}{config.unit ? ` · ${config.unit}` : ""}
                                </Typography>
                                <ParameterHelp parameterKey={String(key)} />
                              </Stack>
                            )}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                </Popover>
              </>
            ) : (
              <ToggleButtonGroup
                exclusive
                value={selectedAttribute ? String(selectedAttribute.key) : false}
                onChange={(_, value: string | null) => {
                  if (value) handleAttributeToggle(value);
                }}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  "& .MuiToggleButtonGroup-grouped": {
                    flex: "1 1 calc(50% - 6px)",
                    minWidth: 0,
                    margin: 0,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark" ? "#2b5b68" : "#d7e8e3",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(166, 232, 210, 0.18)"
                        : "rgba(22, 105, 81, 0.10)",
                    color: "text.primary",
                  },
                }}
                aria-label="Atributo de la estación"
              >
                {availableAttributes.map(({ key, config }) => {
                  const AttributeIcon = config.icon;
                  return (
                    <ToggleButton
                      key={String(key)}
                      value={String(key)}
                      aria-label={`Seleccionar ${config.label}`}
                      sx={{
                        minHeight: 124,
                        p: 1.25,
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                        textAlign: "center",
                        whiteSpace: "normal",
                        borderRadius: 2,
                        backgroundColor: "transparent",
                      }}
                    >
                      <AttributeIcon
                        aria-hidden="true"
                        sx={{
                          fontSize: 34,
                          color: "inherit",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight={800}
                        sx={{
                          width: "100%",
                          maxWidth: "18ch",
                          marginInline: "auto",
                          whiteSpace: "normal",
                          overflowWrap: "normal",
                          lineHeight: 1.15,
                          textAlign: "center",
                        }}
                      >
                        {config.label}
                      </Typography>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            )}

          </Box>
          {latestSourceStatus && latestSourceStatus !== "publicado" && (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Última fila de la fuente: <strong>{latestSourceStatus.replaceAll("_", " ")}</strong>. El valor numérico visible, si existe, corresponde a una medición anterior.
            </Alert>
          )}
          {selectedAttribute && latestPoint && (
            <Box
              sx={{
                p: 1.75,
                borderRadius: 3,
                border: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark" ? "#2b5b68" : "#d7e8e3",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(7, 29, 38, 0.55)"
                    : "rgba(237, 245, 243, 0.78)",
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" fontWeight={800}>
                  {selectedAttribute.config.label}
                </Typography>
                <ParameterHelp
                  parameterKey={String(selectedAttribute.key)}
                  unit={selectedAttribute.config.unit}
                />
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.04em" }}>
                {latestPoint.value.toLocaleString("es-UY", { maximumFractionDigits: 2 })} {selectedAttribute.config.unit}
              </Typography>
              <Typography variant="caption" display="block">
                {formatOptionalDate(latestPoint.timestamp)} — Medición
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {formatOptionalDate(latestPoint.recvTime)} — Recepción
              </Typography>
              {historyLoading && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Calculando referencia histórica…
                </Typography>
              )}
              {historyForAttribute(selectedAttribute) && (
                <Stack spacing={0.25} sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Últimos {historyDays} días · {historyForAttribute(selectedAttribute)?.sampleCount} muestras
                  </Typography>
                  <Typography variant="caption" display="block">
                    Mínimo: {formatHistoricalReference(
                      historyForAttribute(selectedAttribute)?.min,
                      selectedAttribute.config.unit,
                    )}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Máximo: {formatHistoricalReference(
                      historyForAttribute(selectedAttribute)?.max,
                      selectedAttribute.config.unit,
                    )}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Mediana: {formatThresholdValue(
                      historyForAttribute(selectedAttribute)?.percentiles.p50,
                    )} {selectedAttribute.config.unit}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    P05 {formatThresholdValue(historyForAttribute(selectedAttribute)?.percentiles.p05)} · P95 {formatThresholdValue(historyForAttribute(selectedAttribute)?.percentiles.p95)}
                  </Typography>
                </Stack>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => openThresholdConfiguration(selectedAttribute)}
                sx={(theme) => ({
                  mt: 1,
                  alignSelf: "flex-start",
                  textTransform: "none",
                  borderRadius: 2,
                  ...(theme.palette.mode === "dark"
                    ? {
                        color: "#8fe6df",
                        borderColor: "#57d5c4",
                        "&:hover": {
                          borderColor: "#8fe6df",
                          backgroundColor: "rgba(87, 213, 196, 0.12)",
                        },
                      }
                    : {}),
                })}
              >
                Configurar alerta por este parámetro
              </Button>
            </Box>
          )}

          <Box>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                mb: 0.75,
                fontWeight: 800,
                letterSpacing: "0.12em",
                color: "text.secondary",
              }}
            >
              Histórico
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={selectedRangeHours}
              onChange={(_, value: number | null) => {
                if (value !== null) onRangeChange(value);
              }}
              onPointerDown={(event) => event.stopPropagation()}
              onPointerMove={(event) => event.stopPropagation()}
              onPointerUp={(event) => event.stopPropagation()}
              onPointerCancel={(event) => event.stopPropagation()}
              aria-label="Período del histórico"
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                overflowX: "auto",
                gap: 0.75,
                pb: 0.5,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                "& .MuiToggleButtonGroup-grouped": {
                  flexShrink: 0,
                  margin: 0,
                  border: "1px solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark" ? "#2b5b68" : "#d7e8e3",
                  borderRadius: 99,
                },
              }}
            >
              {periodOptions.map((period) => (
                <ToggleButton key={period.value} value={period.value} sx={{ minWidth: 58, minHeight: 44, px: 1.75, borderRadius: 99 }}>
                  {period.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {chartAttributes.length > 0 && (
            <MultiContextHistoricalChart
              title={chartAttributes.length > 1 ? "Comparación de parámetros" : chartAttributes[0].config.label}
              unit={chartAttributes.length > 1 ? undefined : chartAttributes[0].config.unit}
              series={chartAttributes.map((attribute) => ({
                contextId: `${context.id}:${String(attribute.key)}`,
                contextLabel: context.label,
                color: getAttributeChartColor(attribute.config),
                data: attribute.points,
                unit: attribute.config.unit,
                attributeLabel: attribute.config.label,
                yAxisRange: attribute.config.yAxisRange,
              }))}
              multiAxis={isDesktop && chartAttributes.length > 1}
              height={isDesktop ? 300 : 240}
            />
          )}
        </>
      )}
    </Stack>
  );
};


const DEFAULT_RANGE_HOURS = 24 * 7;
const MOBILE_PLACE_LOADING_MIN_MS = 350;

const DashboardContent = () => {
  const isMobile = useResponsiveLayout() === "mobile";
  const authCtx = useContext(AuthContext);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === document.documentElement);
    };
    document.addEventListener("fullscreenchange", syncFullscreenState);
    syncFullscreenState();
    return () => document.removeEventListener("fullscreenchange", syncFullscreenState);
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const contextFromUrl = searchParams.get("context");
  const contextIdFromUrl = contextFromUrl && CONTEXTS.some((context) => context.id === contextFromUrl)
    ? contextFromUrl
    : null;
  const alertAtParam = searchParams.get("alertAt");
  const alertAtFromUrl = alertAtParam ? new Date(alertAtParam) : null;
  const hasAlertAt = Boolean(alertAtFromUrl && !Number.isNaN(alertAtFromUrl.getTime()));
  const alertAtTimestamp = hasAlertAt ? alertAtFromUrl!.getTime() : null;
  const parsedAlertRangeHours = Number(searchParams.get("rangeHours"));
  const alertRangeHours = Number.isFinite(parsedAlertRangeHours) && parsedAlertRangeHours > 0
    ? parsedAlertRangeHours
    : DEFAULT_RANGE_HOURS;
  const attributeKeysFromUrl = useMemo(
    () => searchParams.getAll("attribute").filter(Boolean),
    [searchParams],
  );
  const {
    generalStartDate,
    generalEndDate,
    setGeneralStartDate,
    setGeneralEndDate,
  } = useHistoricalDataContext();
  const [selectedContextIds, setSelectedContextIds] = useState<string[]>(
    () => contextIdFromUrl ? [contextIdFromUrl] : CONTEXTS.map((context) => context.id)
  );
  const [selectedRangeHours, setSelectedRangeHours] = useState<number>(() => hasAlertAt ? alertRangeHours : DEFAULT_RANGE_HOURS);
  const historyDays = Math.max(1, Math.round(selectedRangeHours / 24));
  const [groupByContext, setGroupByContext] = useState<boolean>(true);
  const [focusedContextId, setFocusedContextId] = useState<string | null>(contextIdFromUrl);
  const [focusedPayload, setFocusedPayload] = useState<GeneralServicePayload | null>(null);
  const [focusedLoading, setFocusedLoading] = useState(false);
  const [focusedHistorySummary, setFocusedHistorySummary] = useState<ContextHistorySummary | null>(null);
  const [focusedHistoryLoading, setFocusedHistoryLoading] = useState(false);
  const [selectedAttributeKeys, setSelectedAttributeKeys] = useState<string[]>(() => attributeKeysFromUrl);
  const lastTrackedPlaceRef = useRef<string | null>(null);
  const lastTrackedChartRef = useRef<string | null>(null);
  const lastTrackedFilterRef = useRef<string | null>(null);
  const [placePanelOffset, setPlacePanelOffset] = useState(0);
  const [placePanelStage, setPlacePanelStage] = useState<PlacePanelStage>("expanded");
  const [placePanelDragging, setPlacePanelDragging] = useState(false);
  const [placePanelReady, setPlacePanelReady] = useState(false);
  const [placePanelClosing, setPlacePanelClosing] = useState(false);
  const placePanelOffsetRef = useRef(0);
  const placePanelCloseTimerRef = useRef<number | null>(null);
  const placePanelSwipeRef = useRef<{
    startX: number;
    startY: number;
    dragStartY: number;
    fromHandle: boolean;
    isDragging: boolean;
    axis: "pending" | "horizontal" | "vertical";
  } | null>(null);
  useEffect(() => {
    if (placePanelCloseTimerRef.current !== null) {
      window.clearTimeout(placePanelCloseTimerRef.current);
      placePanelCloseTimerRef.current = null;
    }
    placePanelOffsetRef.current = 0;
    setPlacePanelOffset(0);
    setPlacePanelStage("expanded");
    setPlacePanelDragging(false);
    setPlacePanelClosing(false);
    setPlacePanelReady(false);
    placePanelSwipeRef.current = null;
    if (!focusedContextId) return;
    const frame = window.requestAnimationFrame(() => setPlacePanelReady(true));
    return () => window.cancelAnimationFrame(frame);
  }, [focusedContextId]);
  const [latestRecvByContextId, setLatestRecvByContextId] = useState<Record<string, ContextFreshnessData>>({});
  const [followedPlaceIds, setFollowedPlaceIds] = useState<string[]>(() => loadFollowedPlaceIds());
  const favoritesOnly = searchParams.get("favorites") === "1";
  const visibleContexts = useMemo(
    () => favoritesOnly
      ? CONTEXTS.filter((context) => followedPlaceIds.includes(context.id))
      : CONTEXTS,
    [favoritesOnly, followedPlaceIds],
  );
  const clearAlertWindowParams = (params: URLSearchParams) => {
    params.delete("alertAt");
    params.delete("rangeHours");
    params.delete("attribute");
  };
  const handleAttributeSelectionChange = (keys: string[]) => {
    const nextKeys = Array.from(new Set(keys)).slice(0, MAX_DESKTOP_COMPARISON_ATTRIBUTES);
    setSelectedAttributeKeys(nextKeys);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("attribute");
    nextKeys.forEach((key) => nextParams.append("attribute", key));
    setSearchParams(nextParams, { replace: true });
  };
  const handleShowAllPlaces = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("favorites");
    clearAlertWindowParams(nextParams);
    setSelectedAttributeKeys([]);
    setSearchParams(nextParams, { replace: true });
  };
  const handleShowFollowedPlaces = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("favorites", "1");
    nextParams.delete("context");
    clearAlertWindowParams(nextParams);
    setFocusedContextId(null);
    setFocusedPayload(null);
    setSelectedAttributeKeys([]);
    setSearchParams(nextParams, { replace: true });
  };

  const handleContextSelectionChange = (contextIds: string[]) => {
    setSelectedContextIds(contextIds);
    const nextParams = new URLSearchParams(searchParams);
    if (contextIds.length === 1) {
      nextParams.set("context", contextIds[0]);
    } else {
      nextParams.delete("context");
    }
    clearAlertWindowParams(nextParams);
    setSelectedAttributeKeys([]);
    setSearchParams(nextParams, { replace: true });
  };
  useEffect(() => {
    if (!focusedContextId || lastTrackedPlaceRef.current === focusedContextId) return;
    lastTrackedPlaceRef.current = focusedContextId;
    trackAnalyticsEvent("place_view", {
      place_id: focusedContextId,
      place_category: getPlaceCategoryId(focusedContextId),
    });
  }, [focusedContextId]);

  useEffect(() => {
    const placeId = focusedContextId || "all";
    const metric = selectedAttributeKeys.join(",") || "overview";
    const key = `${placeId}:${metric}:${selectedRangeHours}`;
    if (lastTrackedChartRef.current === key) return;
    lastTrackedChartRef.current = key;
    trackAnalyticsEvent("chart_view", {
      place_id: placeId,
      metric,
      time_range: `${selectedRangeHours}h`,
    });
  }, [focusedContextId, selectedAttributeKeys, selectedRangeHours]);

  useEffect(() => {
    const valueCategory = selectedContextIds.length === CONTEXTS.length
      ? "all"
      : `selected_${selectedContextIds.length}`;
    if (lastTrackedFilterRef.current === valueCategory) return;
    lastTrackedFilterRef.current = valueCategory;
    trackAnalyticsEvent("filter_applied", {
      filter_type: "place_selection",
      value_category: valueCategory,
    });
  }, [selectedContextIds]);


  useEffect(() => {
    if (contextIdFromUrl) {
      setSelectedContextIds([contextIdFromUrl]);
      setGroupByContext(true);
    }
  }, [contextIdFromUrl]);

  useEffect(() => {
    let cancelled = false;
    const baseUrl = process.env.REACT_APP_API_HOST ?? "";
    const metadataKeys: Record<string, true> = {
      timestamp: true,
      recvTime: true,
      latitud: true,
      longitud: true,
      fuente: true,
      playa: true,
      lugar_muestreo: true,
    };

    const loadLatestReception = async () => {
      const results = await Promise.all(
        CONTEXTS.map(async (context) => {
          let latestReceivedAt: string | null | undefined;
          const attributeLatestReceivedAt: Record<string, string | undefined> = {};

          const consumeRows = (rows: Array<Record<string, unknown>>) => {
            rows.forEach((row) => {
              const rawReception = row.recvTime ?? row.timestamp;
              if (typeof rawReception !== "string" || Number.isNaN(Date.parse(rawReception))) return;
              if (!latestReceivedAt || Date.parse(rawReception) > Date.parse(latestReceivedAt)) {
                latestReceivedAt = rawReception;
              }
              Object.entries(row).forEach(([key, value]) => {
                if (metadataKeys[key] || typeof value !== "number" || !Number.isFinite(value)) return;
                const previous = attributeLatestReceivedAt[key];
                if (!previous || Date.parse(rawReception) > Date.parse(previous)) {
                  attributeLatestReceivedAt[key] = rawReception;
                }
              });
            });
          };

          try {
            const response = await axios.get(`${baseUrl}/api/v1/contexts/${context.id}/last`);
            const rows = (response.data?.data ?? []) as Array<Record<string, unknown>>;
            consumeRows(rows);
            if (rows.length === 0 && latestReceivedAt === undefined) latestReceivedAt = null;
          } catch {
            latestReceivedAt = null;
          }

          return [
            context.id,
            { latestReceivedAt, attributeLatestReceivedAt },
          ] as const;
        })
      );

      if (!cancelled) {
        setLatestRecvByContextId(Object.fromEntries(results));
      }
    };

    void loadLatestReception();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setFocusedContextId(contextIdFromUrl);
  }, [contextIdFromUrl]);

  useEffect(() => {
    if (!focusedContextId) return;
    setSelectedRangeHours(hasAlertAt ? alertRangeHours : DEFAULT_RANGE_HOURS);
    const context = CONTEXTS.find((candidate) => candidate.id === focusedContextId);
    const defaultAttribute = context
      ? defaultMobileAttributeForContext(context, attributeKeysFromUrl[0] || null)
      : null;
    setSelectedAttributeKeys(
      attributeKeysFromUrl.length > 0
        ? attributeKeysFromUrl
        : defaultAttribute
          ? [defaultAttribute]
          : [],
    );
  }, [focusedContextId, alertAtParam, attributeKeysFromUrl, alertRangeHours, hasAlertAt]);
  const handlePlaceSelect = (contextId: string) => {
    trackAnalyticsEvent("map_interaction", { interaction_type: "place_select" });
    setFocusedContextId(contextId);
    handleContextSelectionChange([contextId]);
  };

  const handleAdjacentPlace = (direction: -1 | 1) => {
    if (!focusedContextId) return;
    const currentIndex = visibleContexts.findIndex((context) => context.id === focusedContextId);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= visibleContexts.length) return;

    const nextContext = visibleContexts[nextIndex];
    setFocusedContextId(nextContext.id);
    setFocusedPayload(null);
    setSelectedAttributeKeys([]);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("context", nextContext.id);
    clearAlertWindowParams(nextParams);
    setSearchParams(nextParams, { replace: true });
  };

  const handlePlaceClose = () => {
    if (placePanelClosing) return;
    placePanelSwipeRef.current = null;
    placePanelOffsetRef.current = 0;
    setPlacePanelClosing(true);
    placePanelCloseTimerRef.current = window.setTimeout(() => {
      placePanelCloseTimerRef.current = null;
      setPlacePanelClosing(false);
      setPlacePanelOffset(0);
      setFocusedContextId(null);
      setFocusedPayload(null);
      setSelectedAttributeKeys([]);
      setSelectedContextIds(CONTEXTS.map((context) => context.id));
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("context");
      clearAlertWindowParams(nextParams);
      setSearchParams(nextParams, { replace: true });
    }, 260);
  };

  const handlePlacePanelPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest("button, a, input, canvas, [role='button']")) {
      return;
    }
    const fromHandle = Boolean(target.closest("[data-place-panel-handle]"));
    placePanelOffsetRef.current = 0;
    setPlacePanelOffset(0);
    setPlacePanelDragging(false);
    placePanelSwipeRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      dragStartY: event.clientY,
      fromHandle,
      isDragging: false,
      axis: fromHandle ? "vertical" : "pending",
    };
  };

  const handlePlacePanelPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const swipe = placePanelSwipeRef.current;
    if (!swipe) return;

    const deltaX = event.clientX - swipe.startX;
    const pendingDeltaY = event.clientY - swipe.startY;
    if (swipe.axis === "pending") {
      if (Math.max(Math.abs(deltaX), Math.abs(pendingDeltaY)) < 8) return;
      swipe.axis = Math.abs(deltaX) > Math.abs(pendingDeltaY) ? "horizontal" : "vertical";
    }
    if (swipe.axis !== "vertical") return;

    const scrollContainer = event.currentTarget.querySelector<HTMLElement>("[data-place-panel-scroll]");
    if (!swipe.fromHandle && !swipe.isDragging && (scrollContainer?.scrollTop ?? 0) > 0) {
      return;
    }
    let deltaY = event.clientY - swipe.dragStartY;
    if ((!swipe.fromHandle && deltaY <= 0) || (swipe.fromHandle && deltaY === 0)) return;
    if (!swipe.isDragging) {
      swipe.isDragging = true;
      if (!swipe.fromHandle) {
        swipe.dragStartY = event.clientY;
        deltaY = 0;
      }
      setPlacePanelDragging(true);
    }
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
    const nextOffset = Math.max(-160, Math.min(deltaY, 160));
    placePanelOffsetRef.current = nextOffset;
    setPlacePanelOffset(nextOffset);
  };

  const handlePlacePanelPointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const swipe = placePanelSwipeRef.current;
    placePanelSwipeRef.current = null;
    const offset = placePanelOffsetRef.current;
    placePanelOffsetRef.current = 0;
    setPlacePanelDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!swipe?.isDragging) {
      setPlacePanelOffset(0);
      return;
    }
    const snapTarget = getPlacePanelSnapTarget(placePanelStage, offset);
    if (snapTarget === "closed") {
      handlePlaceClose();
      return;
    }
    setPlacePanelStage(snapTarget);
    setPlacePanelOffset(0);
  };

  const handleFollowPlace = async (
    contextId: string,
    attributes: string[],
  ): Promise<FollowToggleResult> => {
    const userEmail = authCtx.userEmail?.trim().toLowerCase();
    if (!userEmail) {
      throw new Error("Necesitás ingresar con una cuenta para seguir esta estación.");
    }
    await activateContextAlerts({
      contextId,
      users: [userEmail],
      attributes,
      token: authCtx.token,
    });
    const next = followedPlaceIds.includes(contextId)
      ? { placeIds: followedPlaceIds, followed: true }
      : toggleFollowedPlaceId(followedPlaceIds, contextId);
    const persisted = saveFollowedPlaceIds(next.placeIds);
    setFollowedPlaceIds(next.placeIds);
    return { followed: next.followed, persisted };
  };

  const handleUnfollowPlace = async (contextId: string): Promise<FollowToggleResult> => {
    const userEmail = authCtx.userEmail?.trim().toLowerCase();
    if (!userEmail) {
      throw new Error("Necesitás ingresar con una cuenta para quitar este seguimiento.");
    }
    await deactivateContextAlerts({
      contextId,
      user: userEmail,
      token: authCtx.token,
    });
    const next = toggleFollowedPlaceId(followedPlaceIds, contextId);
    const persisted = saveFollowedPlaceIds(next.placeIds);
    setFollowedPlaceIds(next.placeIds);
    if (favoritesOnly && focusedContextId === contextId && !next.followed) {
      handlePlaceClose();
    }
    return { followed: next.followed, persisted };
  };

  useEffect(() => {
    const endDate = alertAtTimestamp !== null
      ? new Date(alertAtTimestamp + 1000)
      : new Date();
    const startDate = new Date(
      endDate.getTime() - selectedRangeHours * 60 * 60 * 1000
    );

    setGeneralEndDate(endDate);
    setGeneralStartDate(startDate);
  }, [
    alertAtTimestamp,
    selectedRangeHours,
    setGeneralEndDate,
    setGeneralStartDate,
  ]);

  const fechaDesde = useMemo(
    () => generalStartDate?.toISOString() ?? "",
    [generalStartDate]
  );
  const fechaHasta = useMemo(
    () => generalEndDate?.toISOString() ?? "",
    [generalEndDate]
  );
  useEffect(() => {
    let cancelled = false;
    if (!focusedContextId || !fechaDesde || !fechaHasta) {
      setFocusedPayload(null);
      setFocusedLoading(false);
      return;
    }

    setFocusedPayload(null);
    setFocusedLoading(true);
    const loadingStartedAt = Date.now();
    fetchGeneralData(focusedContextId, fechaDesde, fechaHasta)
      .then((payload) => {
        if (!cancelled) setFocusedPayload(payload);
      })
      .catch((error) => {
        if (!cancelled) console.warn(`Context fetch failed for ${focusedContextId}`, error);
      })
      .finally(() => {
        const remainingMs = Math.max(
          0,
          MOBILE_PLACE_LOADING_MIN_MS - (Date.now() - loadingStartedAt),
        );
        window.setTimeout(() => {
          if (!cancelled) setFocusedLoading(false);
        }, remainingMs);
      });

    return () => {
      cancelled = true;
    };
  }, [fechaDesde, fechaHasta, focusedContextId]);

  useEffect(() => {
    let cancelled = false;
    if (!focusedContextId) {
      setFocusedHistorySummary(null);
      setFocusedHistoryLoading(false);
      return;
    }
    setFocusedHistorySummary(null);
    setFocusedHistoryLoading(true);
    fetchContextHistorySummary(focusedContextId, authCtx.token, historyDays)
      .then((summary) => {
        if (!cancelled) setFocusedHistorySummary(summary);
      })
      .catch((error) => {
        if (!cancelled) console.warn(`History summary failed for ${focusedContextId}`, error);
      })
      .finally(() => {
        if (!cancelled) setFocusedHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authCtx.token, focusedContextId, historyDays]);



  const focusedContext = focusedContextId
    ? CONTEXTS.find((context) => context.id === focusedContextId) || null
    : null;
  const placePanelBaseHeight = placePanelStage === "expanded"
    ? (isFullscreen ? "90vh" : "88vh")
    : placePanelStage === "intermediate"
      ? "54vh"
      : "30vh";
  const placePanelHeight = placePanelDragging && placePanelOffset !== 0
    ? `calc(${placePanelBaseHeight} - ${placePanelOffset}px)`
    : placePanelBaseHeight;
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        height: isMobile ? undefined : "calc(100vh - 64px)",
        minHeight: isMobile ? undefined : 560,
        p: 0,
      }}
    >
      <Box sx={{ width: "100%", height: "100%" }}>
        <>
          <ContextSelectionCard
            contexts={visibleContexts}
            selectedContextIds={selectedContextIds}
            onSelectionChange={handleContextSelectionChange}
            onPlaceSelect={handlePlaceSelect}
            latestRecvByContextId={latestRecvByContextId}
            selectedRangeHours={selectedRangeHours}
            onRangeChange={setSelectedRangeHours}
            groupByContext={groupByContext}
            onGroupByContextChange={setGroupByContext}
            mobile
            mapViewport={isMobile ? "mobile" : "desktop"}
            favoritesOnly={favoritesOnly}
            onShowFollowedPlaces={handleShowFollowedPlaces}
            onShowAllPlaces={handleShowAllPlaces}
          />
          {focusedContext && (
            <Box
              component="section"
              aria-label={`Ficha de ${focusedContext.label}`}
              onPointerDown={handlePlacePanelPointerDown}
              onPointerMove={handlePlacePanelPointerMove}
              onPointerUp={handlePlacePanelPointerEnd}
              onPointerCancel={handlePlacePanelPointerEnd}
              sx={{
                position: isMobile ? "fixed" : "absolute",
                zIndex: 1200,
                left: isMobile ? { xs: 0, sm: 16 } : 16,
                right: isMobile ? { xs: 0, sm: 16 } : 16,
                bottom: isMobile ? "env(safe-area-inset-bottom)" : 16,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                // The panel reaches the viewport edge on mobile; desktop keeps a map margin.
                height: placePanelHeight,
                transform: placePanelClosing || !placePanelReady
                  ? "translateY(100%)"
                  : "translateY(0)",
                transition: placePanelClosing
                  ? "transform 260ms cubic-bezier(0.22, 1, 0.36, 1)"
                  : placePanelDragging
                    ? "none"
                    : placePanelReady
                      ? "height 220ms cubic-bezier(0.22, 1, 0.36, 1), transform 280ms cubic-bezier(0.22, 1, 0.36, 1)"
                      : "none",
                touchAction: "pan-y",
                borderRadius: { xs: "24px 24px 0 0", sm: 3 },
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(16, 45, 54, 0.96)"
                    : "rgba(252, 254, 253, 0.97)",
                color: "text.primary",
                border: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark" ? "#2b5b68" : "#d7e8e3",
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 -18px 48px rgba(3, 15, 27, 0.42)"
                    : "0 -18px 48px rgba(18, 70, 61, 0.18)",
                backdropFilter: "blur(18px) saturate(125%)",
              }}
            >
              <Box
                data-place-panel-handle
                aria-hidden="true"
                sx={{
                  width: 40,
                  height: 4,
                  borderRadius: 99,
                  mx: "auto",
                  mt: 1,
                  touchAction: "none",
                  userSelect: "none",
                  cursor: "grab",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "#6e9a9b" : "#9abbb4",
                }}
              />
              <Box
                data-place-panel-scroll
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  px: { xs: 2, sm: 2.5 },
                  pt: 1,
                  pb: "calc(16px + env(safe-area-inset-bottom))",
                  overscrollBehavior: "contain",
                }}
              >
                <MobilePlacePanel
                  context={focusedContext}
                  payload={focusedPayload}
                  loading={focusedLoading}
                  historySummary={focusedHistorySummary}
                  historyLoading={focusedHistoryLoading}
                  historyDays={historyDays}
                  selectedAttributeKeys={selectedAttributeKeys}
                  onAttributeSelectionChange={handleAttributeSelectionChange}
                  isDesktop={!isMobile}
                  selectedRangeHours={selectedRangeHours}
                  onRangeChange={setSelectedRangeHours}
                  onClose={handlePlaceClose}
                  onNavigate={handleAdjacentPlace}
                  isFollowed={followedPlaceIds.includes(focusedContext.id)}
                  anonymousUser={isAnonymousUser(authCtx.userEmail)}
                  onFollowPlace={handleFollowPlace}
                  onUnfollowPlace={handleUnfollowPlace}
                  userEmail={authCtx.userEmail || ""}
                  token={authCtx.token}
                  placeIndex={visibleContexts.findIndex((context) => context.id === focusedContext.id)}
                  placeCount={visibleContexts.length}
                />
              </Box>
            </Box>
          )}
        </>
      </Box>
    </Box>
  );
};

export default function Dashboard() {
  return <DashboardContent />;
}
